import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from '@ant-design/compatible';
import { Button, Col, DatePicker, Divider, Drawer, Input, Modal, Row } from 'antd';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { NotificationManager } from 'react-notifications';
import BaseRadioList from '@/components/Elements/BaseRadios';
import BaseSelect from '@/components/Elements/BaseSelect';
import { _newLine, getFirstLetters } from '@/utils/helpers';
import { createDocument, getDocumentsByCategory, updateDocument } from '@/redux/actions/documentActions';
import { getDocumentCategoriesActive } from '@/redux/actions/documentCategorieActions';
import DocumentFileUpload from './DocumentFileUpload';

export class DocumentForm extends Component {
    state = {
        isSubmitting: false,
        originalDocuments: [],
        signedDocuments: [],
        categoryId: null,
        categoryLabel: null,
        serialNumber: null,
        selectedYear: null,
        showDocumentNumber: true,
        isDocumentNumberModalOpen: false
    };

    componentDidUpdate(prevProps) {
        const opened = this.props.visible && !prevProps.visible;
        const changedItem = this.props.visible && prevProps.currentItem?.id !== this.props.currentItem?.id;
        if (opened || changedItem) {
            const currentItem = this.props.currentItem;
            this.setState({
                originalDocuments: currentItem?.original_documents || [],
                signedDocuments: currentItem?.signed_documents || [],
                categoryId: currentItem?.categories_id ? Number(currentItem.categories_id) : null,
                categoryLabel: currentItem?.category?.name || null,
                serialNumber: currentItem?.serial || null,
                selectedYear: currentItem?.year ? String(currentItem.year) : null,
                showDocumentNumber: Number(currentItem?.categories_id) !== 2,
                isDocumentNumberModalOpen: false
            });
            this.props.getDocumentCategoriesActive(currentItem?.categories_id);
        }
    }

    toDate = value => value ? moment(value, 'YYYY-MM-DD') : null;

    submit = event => {
        event.preventDefault();
        this.props.form.validateFields(async (error, values) => {
            if (error) return;
            this.setState({ isSubmitting: true });
            const payload = {
                ...values,
                issue_date: values.issue_date ? values.issue_date.format('YYYY-MM-DD') : null,
                expiry_date: values.expiry_date ? values.expiry_date.format('YYYY-MM-DD') : null,
                original_document: this.state.originalDocuments,
                signed_document: this.state.signedDocuments,
                serial: this.state.serialNumber,
                year: this.state.selectedYear,
                document_number: this.state.showDocumentNumber ? values.document_number : null
            };
            delete payload.yearMonth;
            try {
                if (this.props.currentItem) {
                    await this.props.updateDocument(this.props.currentItem.id, payload);
                } else {
                    await this.props.createDocument(payload);
                }
                this.close();
                this.props.reloadData();
            } catch (requestError) {
                this.setState({ isSubmitting: false });
            }
        });
    };

    close = () => {
        this.props.form.resetFields();
        this.setState({
            isSubmitting: false,
            originalDocuments: [],
            signedDocuments: [],
            categoryId: null,
            categoryLabel: null,
            serialNumber: null,
            selectedYear: null,
            showDocumentNumber: true,
            isDocumentNumberModalOpen: false
        });
        this.props.onCancel();
    };

    handleCategoryChange = (value, label) => {
        const categoryId = value ? Number(value) : null;
        this.setState({
            categoryId,
            categoryLabel: label || null,
            serialNumber: null,
            selectedYear: null,
            showDocumentNumber: categoryId !== 2
        });
        this.props.form.setFieldsValue({ document_number: undefined });
    };

    openDocumentNumberModal = () => {
        if (!this.state.categoryId || !this.state.categoryLabel) {
            NotificationManager.warning('Vui lòng chọn loại văn bản trước khi lấy số!');
            return;
        }
        this.setState({ isDocumentNumberModalOpen: true });
    };

    handleDocumentNumber = event => {
        if (event?.preventDefault) event.preventDefault();
        this.props.form.validateFields(['yearMonth'], async (error, values) => {
            if (error) return;

            try {
                const yearMonth = values.yearMonth.format('YYYY/MM');
                const selectedYear = values.yearMonth.format('YYYY');
                const response = await this.props.getDocumentsByCategory(
                    this.state.categoryId,
                    { year: selectedYear }
                );

                let serialNumber;
                if (this.props.currentItem) {
                    serialNumber = this.props.currentItem.serial || response.data || '001';
                } else {
                    const nextSerial = response.data ? parseInt(response.data, 10) + 1 : 1;
                    serialNumber = String(nextSerial).padStart(3, '0');
                }

                const categoryCode = getFirstLetters(this.state.categoryLabel.toUpperCase());
                this.props.form.setFieldsValue({
                    document_number: `${categoryCode}-${serialNumber}/${yearMonth}`
                });
                this.setState({
                    serialNumber,
                    selectedYear,
                    isDocumentNumberModalOpen: false
                });
            } catch (requestError) {
                NotificationManager.error('Đã xảy ra lỗi khi lấy số tài liệu.');
            }
        });
    };

    render() {
        const { visible, currentItem, categories } = this.props;
        const { getFieldDecorator } = this.props.form;
        const resetKey = currentItem?.id || 'new';
        const currentYearMonth = moment();
        // Khi mở bản ghi lần đầu, render theo dữ liệu hiện tại ngay trước khi state được đồng bộ.
        const showDocumentNumber = currentItem && this.state.categoryId === null
            ? Number(currentItem.categories_id) !== 2
            : this.state.showDocumentNumber;
        return (
            <Drawer
                visible={visible}
                title={currentItem ? 'Sửa bản ghi' : 'Thêm mới bản ghi'}
                onOk={this.submit}
                maskClosable={false}
                width={isMobile ? '100%' : '50%'}
                onClose={this.close}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Button onClick={this.close}>Huỷ</Button>
                        <Divider type="vertical" />
                        <Button type="primary" loading={this.state.isSubmitting} onClick={this.submit}>
                            {currentItem ? 'Cập nhật' : 'Thêm mới'}
                        </Button>
                    </div>
                }
            >
                {visible && (
                    <Form layout="vertical">
                        <Row gutter={16}>
                            <Col xs={24} md={24}>
                                <Form.Item label="Loại văn bản">
                                    {getFieldDecorator('categories_id', {
                                        initialValue: currentItem?.categories_id ? Number(currentItem.categories_id) : null,
                                        normalize: value => value !== undefined ? value : null,
                                        rules: [{ required: true, message: 'Vui lòng chọn!' }]
                                    })(
                                        <BaseSelect
                                            options={categories || []}
                                            optionValue="id"
                                            optionLabel="name"
                                            showSearch
                                            placeholder="Chọn một bản ghi"
                                            className="w-100"
                                            onChange={this.handleCategoryChange}
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            {showDocumentNumber && (
                                <Col xs={24} md={24}>
                                    <Form.Item label="Số văn bản">
                                        <Row gutter={8}>
                                            <Col span={18}>
                                                <Form.Item noStyle>
                                                    {getFieldDecorator('document_number', {
                                                        initialValue: currentItem?.document_number || null,
                                                        rules: [{
                                                            required: true,
                                                            message: 'Bấm vào lấy số để tự động tạo số văn bản!'
                                                        }]
                                                    })(<Input maxLength={255} />)}
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Button type="primary" onClick={this.openDocumentNumberModal}>Lấy số</Button>
                                            </Col>
                                        </Row>
                                    </Form.Item>
                                </Col>
                            )}
                            <Col xs={24} md={24}>
                                <Form.Item label="Tên văn bản">
                                    {getFieldDecorator('name', {
                                        initialValue: currentItem?.name || null,
                                        rules: [{ required: true, whitespace: true, message: 'Vui lòng điền!' }]
                                    })(<Input />)}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Ngày ban hành">
                                    {getFieldDecorator('issue_date', {
                                        initialValue: this.toDate(currentItem?.issue_date),
                                        rules: [{ required: true, message: 'Vui lòng điền!' }]
                                    })(
                                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Chọn ngày" />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Ngày hết hạn">
                                    {getFieldDecorator('expiry_date', {
                                        initialValue: this.toDate(currentItem?.expiry_date),
                                        rules: [{
                                            validator: (_, value, callback) => {
                                                const issueDate = this.props.form.getFieldValue('issue_date');
                                                if (value && issueDate && value.isBefore(issueDate, 'day')) {
                                                    callback('Ngày hết hạn không được trước ngày ban hành.');
                                                } else callback();
                                            }
                                        }]
                                    })(<DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Chọn ngày" />)}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Tệp gốc">
                                    <DocumentFileUpload
                                        resetKey={`original-${resetKey}`}
                                        value={currentItem?.original_documents || []}
                                        onChange={originalDocuments => this.setState({ originalDocuments })}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Tệp đã ký">
                                    <DocumentFileUpload
                                        resetKey={`signed-${resetKey}`}
                                        value={currentItem?.signed_documents || []}
                                        onChange={signedDocuments => this.setState({ signedDocuments })}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Mô tả nội dung">
                                    {getFieldDecorator('description', {
                                        initialValue: currentItem ? _newLine(currentItem.description) : null
                                    })(<Input.TextArea rows={4} />)}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Nơi lưu bản gốc">
                                    {getFieldDecorator('file_location', {
                                        initialValue: currentItem?.file_location || null
                                    })(<Input.TextArea />)}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Ghi chú">
                                    {getFieldDecorator('note', {
                                        initialValue: currentItem ? _newLine(currentItem.note) : null
                                    })(<Input.TextArea />)}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Trạng thái">
                                    {getFieldDecorator('status', {
                                        initialValue: currentItem ? Number(currentItem.status) : 1,
                                        rules: [{ required: true, message: 'Vui lòng chọn!' }]
                                    })(<BaseRadioList options={[
                                        { label: 'Active', value: 1 },
                                        { label: 'Inactive', value: 0 }
                                    ]} />)}
                                </Form.Item>
                            </Col>
                        </Row>
                        <Modal
                            visible={this.state.isDocumentNumberModalOpen}
                            title="Chọn năm tháng"
                            maskClosable={false}
                            width={isMobile ? '100%' : '25%'}
                            onCancel={() => this.setState({ isDocumentNumberModalOpen: false })}
                            onOk={this.handleDocumentNumber}
                        >
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="Năm và Tháng" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                                        {getFieldDecorator('yearMonth', {
                                            initialValue: currentYearMonth,
                                            rules: [{ required: true, message: 'Vui lòng chọn năm và tháng!' }]
                                        })(
                                            <DatePicker
                                                picker="month"
                                                style={{ width: '100%' }}
                                                format="YYYY-MM"
                                                placeholder="Chọn năm và tháng"
                                            />
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Modal>
                    </Form>
                )}
            </Drawer>
        );
    }
}

const mapStateToProps = state => ({
    categories: state.documentCategorieReducer.document_categories_active
});

const mapDispatchToProps = dispatch => ({
    getDocumentCategoriesActive: includeId => dispatch(getDocumentCategoriesActive(
        includeId ? { include_id: includeId } : undefined
    )),
    createDocument: data => dispatch(createDocument(data)),
    updateDocument: (id, data) => dispatch(updateDocument(id, data)),
    getDocumentsByCategory: (categoryId, filter) => dispatch(getDocumentsByCategory(categoryId, filter))
});

export default connect(mapStateToProps, mapDispatchToProps)(
    Form.create({ name: 'documentForm' })(DocumentForm)
);
